import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { TokenRequest, TokenResult } from "../domain/entities/Main";
import { usePopup } from "../../../shared/hooks/usePopup";
import { useLoading } from "../../../shared/hooks/useLoading";
import { useAuth } from "../../../shared/hooks/useAuth";
import MainUseCase from "../domain/usecases/MainUseCase";
import MainApi from "../data/MainApi";

export function useToken(tokenParams: TokenRequest | null | undefined) {
  const { showPopup, handleErrorResponse } = usePopup();
  const { showLoading } = useLoading();
  const { token, setToken } = useAuth();
  const [isTokenLoading, setIsTokenLoading] = useState<boolean>(true);
  const [tokenResponse, setTokenResponse] = useState<
    TokenResult | null | undefined
  >(token ?? null);

  const repository = new MainUseCase(new MainApi());

  const {
    isLoading,
    data: tokenResult,
    error,
    isError,
  } = useQuery({
    enabled: !!tokenParams && !token, // Only fetch when token exists
    refetchOnWindowFocus: false,
    retry: false,
    queryKey: ["token", tokenParams],
    queryFn: async () => repository.token(tokenParams as TokenRequest),
  });

  useEffect(() => {
    if (token) {
      setIsTokenLoading(false);
      setTokenResponse(token);
    } else if (!isLoading && tokenResult) {
      setIsTokenLoading(false);
      setTokenResponse(tokenResult);
      setToken(tokenResult);
    }
    showLoading(isTokenLoading);
  }, [isLoading, tokenResult, token, isTokenLoading]);

  useEffect(() => {
    if (error && error instanceof AxiosError && isError) {
      handleErrorResponse(error);
    }
  }, [error, isError, showPopup]);

  return { tokenResponse, isTokenLoading };
}
